import React, { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { FaMapMarkerAlt, FaStar, FaPhone, FaEnvelope, FaUser, FaCertificate } from "react-icons/fa";
import { userAPI } from "../services/api";
import DOMPurify from 'dompurify';


const NurseProfile = () => {
  const navigate = useNavigate();

  const handleGoBack = () => {
    navigate(-1);
  };


  const { id } = useParams();
  
  // Sample nurse data as fallback
  const sampleNurseData = {
    name: "Lilly",
    specialization: "Registered Nurse",
    isCertified: true,
    experience: "5+ years",
    languages: "English, Hindi",
    description: ["Experienced nurse with focus on patient care and treatment"],
    image: "https://images.unsplash.com/photo-1559839734-2b71ea197ec2?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=100&q=80"
  };

  const [nurseData, setNurseData] = useState(sampleNurseData);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Fetch nurse data from API
  useEffect(() => {
    const fetchNurse = async () => {
      try {
        setLoading(true);
        setError(null);

        const response = await userAPI.filterUsers({
          role: 'nurse',
          page: 1,
          limit: 100 // Get more nurses to find the specific one
        });
        
        const nursesData = response.users || [];
        
        // Find the specific nurse by ID
        const specificNurse = nursesData.find(nurse => nurse._id === id);
        
        if (specificNurse) {
          // Transform API data to match the expected format
          const transformedNurse = {
            id: specificNurse._id,
            qualifications: specificNurse.qualifications,
            name: specificNurse.name || "Nurse",
            isCertified: specificNurse.isCertified || false,
            experience: specificNurse.experience || "Experience not specified",
            languages: specificNurse.languages || "English",
            gender: specificNurse.gender || "Not specified",
            description: specificNurse.description || `${specificNurse.name} is an experienced ${specificNurse.role} with a focus on patient care and treatment.`,
            image: specificNurse.profileImage 
              ? (specificNurse.profileImage.startsWith('https') 
                  ? specificNurse.profileImage 
                  : `https://backend.adhdrapidcare.com${specificNurse.profileImage}`)
              : "https://images.unsplash.com/photo-1559839734-2b71ea197ec2?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=100&q=80",
            phone: specificNurse.phone || "Not provided",
            email: specificNurse.email || "Not provided",
            location: specificNurse.location || "Location not specified"
          };
           setNurseData(transformedNurse);

        } else {
          setError("Nurse not found");
        }
      } catch (err) {
        console.error("Error fetching nurse:", err);
        setError("Failed to load nurse profile");
      } finally {
        setLoading(false);
      }
    };

    if (id) {
      fetchNurse();
    }
  }, [id]);

  const handleCheckAvailability = () => {
    navigate(`/nurses/details/${id}`);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading nurse profile...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <p className="text-red-600 mb-4">{error}</p>
          <button 
            onClick={() => navigate(-1)}
            className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
          >
            Go Back
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col">
      {/* Main Content */}
      <main className="flex-grow py-12 container mx-auto px-4 sm:px-6 lg:px-8">
         <div className="flex items-center gap-6 mb-5">
          <button
            onClick={handleGoBack}
            className="flex items-center justify-center w-10 h-10 rounded-full bg-white border border-gray-200 hover:bg-gray-50 transition-colors"
          >
            <svg
              className="w-5 h-5 text-gray-600"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth="2"
                d="M15 19l-7-7 7-7"
              />
            </svg>
          </button>
          <div>
            <h1 className="text-xl md:text-2xl font-semibold text-gray-800">
              Nurse Profile
            </h1>
          </div>
        </div>


          {/* Profile Content */}
          <div className="bg-white rounded-lg shadow-lg overflow-hidden p-6">
              <div className="flex flex-col lg:flex-row">
                {/* Profile Picture Section - Left Side (1/3 width) */}
                <div className="lg:w-1/2 flex flex-col items-center justify-start mr-6">
                  <div className="w-3/4 h-full relative rounded-lg overflow-hidden bg-transparent flex items-start justify-center">
                  <img
                    className="max-w-full object-contain transition-all duration-300 cursor-pointer"
                    src={nurseData.image}
                    alt={`${nurseData.name} Profile`}
                  />
                  </div>
                </div>

                {/* Details Section - Right Side (2/3 width) */}
                <div className="flex flex-col item-center justify-between lg:w-1/2 p-1 pl-5">
                  <div>
                      {/* User Identification */}
                      <div className="mb-6 flex items-center justify-center">
                        <div className="flex flex-col items-center text-center">
                          <h3 class="font-semibold text-xl text-gray-900 text-left mt-4">
                            {nurseData.name}
                          </h3>
                          <p className="text-gray-600 text-sm mt-2">
                            {nurseData.qualifications || "Not Availble"}
                          </p>
                        </div>
                      </div>

                      {/* Profile Information */}
                      <div className="space-y-6 mb-8">
                        {/* Description */}
                        <div className="space-y-3"> 
                          {nurseData.description?.map((desc, index) => (
                            <p key={index} className="text-gray-700 leading-relaxed">
                              {desc}
                            </p>
                          ))}
                        </div>
                      </div> 
                  </div>

                    {/* Action Button */}
                    <div className="flex w-full items-center justify-center mt-8">
                      <div
                        onClick={handleCheckAvailability}
                        className="text-blue-600 leading-relaxed hover:underline cursor-pointer"
                      >
                        Check Availability
                      </div>
                    </div> 

                </div>

              </div>
              
   
          </div>

      </main>
    </div>


  );
};

export default NurseProfile;
